import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminSkills, type SkillWithCount, type DisciplineSkillGroup } from "../../hooks/useAdminSkills";
import { AIGenerationModal } from "../../components/admin/AIGenerationModal";
import type { DisciplineSlug } from "@jumpbean/shared";

/**
 * SkillsPage - Admin page to view skills taxonomy and coverage.
 *
 * Features:
 * - Skills organized by discipline with expand/collapse
 * - Challenge counts per skill
 * - Coverage gap highlighting
 * - Quick "Generate for Skill" action
 * - Link to filtered challenges list per skill
 */
export function SkillsPage() {
  const {
    skillGroups,
    isLoading,
    error,
    expandedDisciplines,
    toggleDiscipline,
    expandAll,
    collapseAll,
    getSkillsWithGaps,
    getTotalStats,
    refetch,
  } = useAdminSkills();

  const [showAIModal, setShowAIModal] = useState(false);
  const [preSelectedDiscipline, setPreSelectedDiscipline] = useState<DisciplineSlug | undefined>();
  const [preSelectedSkill, setPreSelectedSkill] = useState<string | undefined>();

  const stats = getTotalStats();
  const coverageGaps = getSkillsWithGaps(5);

  const handleGenerateForSkill = (skill: SkillWithCount, disciplineSlug: DisciplineSlug) => {
    setPreSelectedDiscipline(disciplineSlug);
    setPreSelectedSkill(skill.skillKey);
    setShowAIModal(true);
  };

  const handleGenerateComplete = () => {
    refetch();
    setShowAIModal(false);
    setPreSelectedDiscipline(undefined);
    setPreSelectedSkill(undefined);
  };

  const handleCloseModal = () => {
    setShowAIModal(false);
    setPreSelectedDiscipline(undefined);
    setPreSelectedSkill(undefined);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Skills Taxonomy</h1>
          <p className="mt-1 text-sm text-gray-500">
            Overview of learning skills and challenge coverage
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Skills"
          value={stats.totalSkills}
          icon={<SkillIcon />}
          color="magic"
        />
        <StatCard
          label="Total Challenges"
          value={stats.totalChallenges}
          icon={<ChallengesIcon />}
          color="green"
        />
        <StatCard
          label="Skills with Content"
          value={stats.skillsWithContent}
          icon={<CheckIcon />}
          color="blue"
        />
        <StatCard
          label="Coverage"
          value={`${stats.coveragePercent}%`}
          icon={<ChartIcon />}
          color={stats.coveragePercent < 50 ? "red" : stats.coveragePercent < 80 ? "yellow" : "green"}
        />
      </div>

      {/* Coverage gaps warning */}
      {coverageGaps.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5">
              <WarningIcon />
            </div>
            <div>
              <h3 className="text-sm font-medium text-amber-800">
                Coverage Gaps Detected
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                {coverageGaps.length} skill{coverageGaps.length > 1 ? "s have" : " has"} fewer than 5 challenges.
                Consider generating more content for these skills.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {coverageGaps.slice(0, 5).map((skill) => (
                  <span
                    key={skill.skillKey}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
                  >
                    {skill.displayName} ({skill.challengeCount})
                  </span>
                ))}
                {coverageGaps.length > 5 && (
                  <span className="text-xs text-amber-600">
                    +{coverageGaps.length - 5} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Skills by discipline */}
      <div className="space-y-4">
        {isLoading ? (
          // Loading skeleton
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-24" />
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-16" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          skillGroups.map((group) => (
            <DisciplineSection
              key={group.disciplineSlug}
              group={group}
              isExpanded={expandedDisciplines.has(group.disciplineSlug)}
              onToggle={() => toggleDiscipline(group.disciplineSlug)}
              onGenerateForSkill={handleGenerateForSkill}
            />
          ))
        )}
      </div>

      {/* AI Generation Modal */}
      {showAIModal && (
        <AIGenerationModal
          isOpen={showAIModal}
          onClose={handleCloseModal}
          onSaveComplete={handleGenerateComplete}
          initialDiscipline={preSelectedDiscipline}
          initialSkills={preSelectedSkill ? [preSelectedSkill] : undefined}
        />
      )}
    </div>
  );
}

// ----- Components -----

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: "magic" | "green" | "blue" | "red" | "yellow";
}

function StatCard({ label, value, icon, color }: StatCardProps) {
  const colorClasses = {
    magic: "bg-magic-50 text-magic-600",
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    red: "bg-red-50 text-red-600",
    yellow: "bg-amber-50 text-amber-600",
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <div className="w-5 h-5">{icon}</div>
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

interface DisciplineSectionProps {
  group: DisciplineSkillGroup;
  isExpanded: boolean;
  onToggle: () => void;
  onGenerateForSkill: (skill: SkillWithCount, disciplineSlug: DisciplineSlug) => void;
}

const DISCIPLINE_COLORS: Record<DisciplineSlug, { bg: string; text: string; border: string }> = {
  math: { bg: "bg-magic-100", text: "text-magic-700", border: "border-magic-200" },
  spelling: { bg: "bg-pink-100", text: "text-pink-700", border: "border-pink-200" },
  patterns: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200" },
  typing: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200" },
  programming: { bg: "bg-green-100", text: "text-green-700", border: "border-green-200" },
};

function DisciplineSection({ group, isExpanded, onToggle, onGenerateForSkill }: DisciplineSectionProps) {
  const colors = DISCIPLINE_COLORS[group.disciplineSlug];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header - clickable */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
            <DisciplineIcon discipline={group.disciplineSlug} className={`w-5 h-5 ${colors.text}`} />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900">{group.disciplineName}</h3>
            <p className="text-sm text-gray-500">
              {group.skills.length} skills, {group.totalChallenges} challenges
            </p>
          </div>
        </div>
        <ChevronIcon
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
        />
      </button>

      {/* Skills list */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {group.skills.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              <p className="text-sm">No skills defined for this discipline.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {group.skills.map((skill) => (
                <SkillRow
                  key={skill.id}
                  skill={skill}
                  disciplineSlug={group.disciplineSlug}
                  disciplineColors={colors}
                  onGenerate={() => onGenerateForSkill(skill, group.disciplineSlug)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface SkillRowProps {
  skill: SkillWithCount;
  disciplineSlug: DisciplineSlug;
  disciplineColors: { bg: string; text: string; border: string };
  onGenerate: () => void;
}

function SkillRow({ skill, disciplineSlug, disciplineColors, onGenerate }: SkillRowProps) {
  const hasGap = skill.challengeCount < 5;
  const isEmpty = skill.challengeCount === 0;

  return (
    <div className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex items-center justify-center w-8 h-8 rounded text-sm font-medium text-gray-500 bg-gray-100">
          {skill.difficultyOrder}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">{skill.displayName}</span>
            {hasGap && (
              <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                  isEmpty ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                }`}
              >
                {isEmpty ? "No content" : "Low coverage"}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 font-mono">{skill.skillKey}</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Challenge count with link */}
        <Link
          to={`/admin/challenges?discipline=${disciplineSlug}&skillTags=${skill.skillKey}`}
          className={`
            inline-flex items-center gap-1 px-2.5 py-1 rounded-lg
            ${disciplineColors.bg} ${disciplineColors.text} ${disciplineColors.border}
            border font-medium text-sm
            hover:opacity-80 transition-opacity
          `}
        >
          <span>{skill.challengeCount}</span>
          <span className="text-xs opacity-75">challenges</span>
        </Link>

        {/* Generate button */}
        <button
          onClick={onGenerate}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-sm font-medium text-magic-600 hover:text-magic-700 hover:bg-magic-50 rounded-lg transition-colors"
          title="Generate content for this skill"
        >
          <SparklesIcon className="w-4 h-4" />
          Generate
        </button>
      </div>
    </div>
  );
}

// ----- Icons -----

function SkillIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
    </svg>
  );
}

function ChallengesIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path
        fillRule="evenodd"
        d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className || "w-5 h-5"}>
      <path d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744l.631 2.522a1.002 1.002 0 00.7.7l2.523.631a1 1 0 010 1.938l-2.522.631a1.002 1.002 0 00-.7.7l-.632 2.523a1 1 0 01-1.938 0l-.631-2.522a1.002 1.002 0 00-.7-.7l-2.522-.631a1 1 0 010-1.938l2.522-.631a1.002 1.002 0 00.7-.7l.631-2.522A1 1 0 0112 2z" />
    </svg>
  );
}

function DisciplineIcon({ discipline, className }: { discipline: DisciplineSlug; className?: string }) {
  switch (discipline) {
    case "math":
      return (
        <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
          <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11a1 1 0 11-2 0 1 1 0 012 0zm-1-3a1 1 0 00-1 1H7a3 3 0 116 0 2.987 2.987 0 01-1.293 2.456l-.387.29a.8.8 0 00-.32.64v.114a1 1 0 01-2 0v-.114c0-.659.31-1.28.84-1.676l.387-.29A1 1 0 0011 11H9a1 1 0 001-1z" />
        </svg>
      );
    case "spelling":
      return (
        <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
            clipRule="evenodd"
          />
        </svg>
      );
    case "patterns":
      return (
        <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
          <path
            fillRule="evenodd"
            d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 2V5h1v1H5zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zm2 2v-1h1v1H5zM13 3a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V4a1 1 0 00-1-1h-3zm1 2v1h1V5h-1zM12 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-3zm2 2v-1h1v1h-1z"
            clipRule="evenodd"
          />
        </svg>
      );
    case "typing":
      return (
        <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
          <path
            fillRule="evenodd"
            d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm2 1a1 1 0 011-1h1a1 1 0 110 2H6a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2h-1zm4 0a1 1 0 100 2h1a1 1 0 100-2h-1zM6 9a1 1 0 000 2h1a1 1 0 000-2H6zm4 0a1 1 0 100 2h1a1 1 0 100-2h-1zm4 0a1 1 0 100 2h1a1 1 0 100-2h-1zM5 13a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z"
            clipRule="evenodd"
          />
        </svg>
      );
    case "programming":
      return (
        <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
          <path
            fillRule="evenodd"
            d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    default:
      return null;
  }
}

export default SkillsPage;
